import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Collapse,
  IconButton,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useState } from 'react';

interface ParsedRow {
  date: string;
  amount: number;
  category?: string;
  type: 'income' | 'expense';
  error?: string;
  rowNumber: number;
}

interface ImportResultData {
  success: ParsedRow[];
  errors: ParsedRow[];
  duplicates: ParsedRow[];
  total: number;
}

interface ImportResultProps {
  result: ImportResultData;
  accountType: 'general' | 'reserve';
}

const ImportResult: React.FC<ImportResultProps> = ({ result, accountType }) => {
  const [showErrors, setShowErrors] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(amount);
  };

  return (
    <Box>
      {/* サマリーカード */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 150px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {result.total}
            </Typography>
            <Typography variant="body2">総処理件数</Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: '1 1 150px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" color="success.main">
              {result.success.length}
            </Typography>
            <Typography variant="body2">インポート成功</Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 150px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <ErrorIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
            <Typography variant="h4" color="error.main">
              {result.errors.length}
            </Typography>
            <Typography variant="body2">エラー</Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 150px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Warning sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" color="warning.main">
              {result.duplicates.length}
            </Typography>
            <Typography variant="body2">重複スキップ</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* 状態別詳細 */}
      
      {/* 成功データ */}
      {result.success.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                インポート成功 ({result.success.length}件)
              </Typography>
              <IconButton onClick={() => setShowSuccess(!showSuccess)}>
                {showSuccess ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={showSuccess}>
              <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 300 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>日付</TableCell>
                      <TableCell>種別</TableCell>
                      <TableCell align="right">金額</TableCell>
                      <TableCell>カテゴリ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.success.slice(0, 50).map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>
                          <Chip 
                            label={row.type === 'income' ? '収入' : '支出'}
                            color={row.type === 'income' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            color={row.type === 'income' ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {formatCurrency(row.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.category || 'その他'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {result.success.length > 50 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  最初の50件を表示しています。全{result.success.length}件がインポートされました。
                </Alert>
              )}
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* エラーデータ */}
      {result.errors.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                エラー ({result.errors.length}件)
              </Typography>
              <IconButton onClick={() => setShowErrors(!showErrors)}>
                {showErrors ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={showErrors}>
              <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 300 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>行番号</TableCell>
                      <TableCell>日付</TableCell>
                      <TableCell>金額</TableCell>
                      <TableCell>エラー内容</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.date || '未設定'}</TableCell>
                        <TableCell>{row.amount > 0 ? formatCurrency(row.amount) : '未設定'}</TableCell>
                        <TableCell>
                          <Typography color="error" variant="body2">
                            {row.error}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* 重複データ */}
      {result.duplicates.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="warning" />
                重複スキップ ({result.duplicates.length}件)
              </Typography>
              <IconButton onClick={() => setShowDuplicates(!showDuplicates)}>
                {showDuplicates ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            <Collapse in={showDuplicates}>
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                以下の取引は既にデータベースに存在するためスキップされました。
              </Alert>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>日付</TableCell>
                      <TableCell>種別</TableCell>
                      <TableCell align="right">金額</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.duplicates.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>
                          <Chip 
                            label={row.type === 'income' ? '収入' : '支出'}
                            color={row.type === 'income' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            color={row.type === 'income' ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {formatCurrency(row.amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* 最終的な結果メッセージ */}
      <Alert 
        severity={result.errors.length === 0 ? 'success' : result.success.length > 0 ? 'warning' : 'error'}
        sx={{ mt: 2 }}
      >
        {result.errors.length === 0 && result.duplicates.length === 0 && 
          `全${result.total}件のデータが正常にインポートされました。`}
        {result.errors.length === 0 && result.duplicates.length > 0 && 
          `${result.success.length}件がインポートされ、${result.duplicates.length}件が重複のためスキップされました。`}
        {result.errors.length > 0 && result.success.length > 0 && 
          `${result.success.length}件がインポートされましたが、${result.errors.length}件でエラーが発生しました。`}
        {result.errors.length > 0 && result.success.length === 0 && 
          `全${result.total}件でエラーが発生し、インポートできませんでした。`}
      </Alert>
    </Box>
  );
};

export default ImportResult;